import { useState, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import type { IPipelineProject } from '@sg/pages/StudioManagement/PortfolioOverview/components/PipelineCalendar/type';
import { getCascadeProjectDatas } from '@gp/utils/project';
import { getInDevProjectDataAnalytics } from '@sg/services';
import { DATA_ANALYTICS_METRICS_LIST } from '@sg/pages/StudioManagement/InDevProjects/pageConfig';

// Hardcoded by product decision: use the first pre-environment project as the
// reference target when querying available InDev datatime.
// const INDEV_PROJECT_ID = 18;

// Studio/Project 二级级联配置
const cascadeInfoList = [
  { level: 1, key: 'studio_id', nameKey: 'studio_name', title: 'Studio', isEndPanel: false },
  { level: 2, key: 'project_id', nameKey: 'game_title', title: 'Project', isEndPanel: true },
];

export const useInDevAnalytics = () => {
  const [selectedMonth, setSelectedMonth] = useState<any>('');
  // 级联筛选器当前选中值
  const [cascadeValue, setCascadeValue] = useState<string[]>([]);
  const [statusValue] = useState<string[]>([]);
  const [milestoneValue] = useState<string[]>([]);

  const [projects, setProjects] = useState<IPipelineProject[]>([]);
  const [projectListAll, setProjectListAll] = useState<IPipelineProject[]>([]);
  const [isMoM, setIsMoM] = useState(true);
  const [loading, setLoading] = useState(false);
  const [studioProjectList, setStudioProjectList] = useState<any[]>([]);
  const [totalPa, setTotalPa] = useState(0);
  const [sortParams, setSortParams] = useState<{ key: string; value: string }[]>([
    { key: 'spend_to_date', value: 'desc' },
  ]);

  // 将 studioProjectList 转为级联树结构
  const cascadeTreeOptions = useMemo(() => {
    if (!studioProjectList || studioProjectList.length === 0) {
      return [];
    }
    // 转换 projects → child 以适配 getCascadeProjectDatas
    // studioProjectList 已由 getFormatProjectDatas 添加了 project_id
    // 将 studio_name 为空的项目合并到 "Others" 节点下，并将 Others 排在最末尾
    const OTHERS_LABEL = 'Others';
    const OTHERS_STUDIO_ID = '__others__';
    const namedStudios: any[] = [];
    const othersProjects: any[] = [];

    studioProjectList.forEach((studio: any) => {
      const studioName = studio.studio_name || studio.studio; // indev api use studio_name instead of studio
      const projectChildren = (studio.projects || []).map((p: any) => ({
        game_title: p.game_title,
        cover: p.cover,
        project_id: p.project_id,
      }));
      if (!studioName) {
        othersProjects.push(...projectChildren);
        return;
      }
      namedStudios.push({
        studio_id: studio.studio_id,
        studio_name: studioName,
        child: projectChildren,
      });
    });

    const transformed = [...namedStudios];
    if (othersProjects.length > 0) {
      transformed.push({
        studio_id: OTHERS_STUDIO_ID,
        studio_name: OTHERS_LABEL,
        child: othersProjects,
      });
    }
    return getCascadeProjectDatas(transformed, '', cascadeInfoList);
  }, [studioProjectList, cascadeInfoList]);

  const turnDate = (date: moment.MomentInput) => {
    return date ? moment(date).format('YYYY-MM') : '';
  };

  const fetchData = async (
    selectedMonthValue: moment.MomentInput,
    isFirst: boolean = false,
    sortParamsValue = sortParams,
  ) => {
    setLoading(true);
    const selectedMonthStr = turnDate(selectedMonthValue);

    const params = {
      month: selectedMonthStr,
      studio_id: [],
      project_id: [],
      metrics: DATA_ANALYTICS_METRICS_LIST,
      include_mom: 1,
      sort: sortParamsValue,
      page: 1,
      page_size: 50,
    };

    try {
      const response: any = await getInDevProjectDataAnalytics(params);
      const responseData = response?.data?.data || response?.data || response || {};
      const projectList = (responseData?.list || []).map((project: any) => ({
        ...project,
        game_title: project?.game_title || project?.game_name || '', // we format to game_title here instead of direct using game_name from api because most our code alrd use game_title
      }));
      const allProject = (responseData?.studios || []).map((studio: any) => ({
        studio_id: studio?.studio_id,
        studio_name: studio?.studio_name || '',
        projects: (studio?.projects || []).map((project: any) => ({
          project_id: String(project?.project_id || ''),
          game_title: project?.game_title || project?.game_name || '',
        })),
      }));

      setProjectListAll(projectList);

      setStudioProjectList(allProject);

      setTotalPa(responseData?.total);

      if (isFirst) {
        setSelectedMonth(moment(responseData?.month));
      }

      changeFilterProjects(cascadeValue, statusValue, milestoneValue, projectList, allProject, selectedMonthValue);
    } catch (error) {
      setProjectListAll([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initSelectedMonth = async () => {
      // 页面初次访问，month传空，后端返回的最新有数据的时间值，会更新渲染时间筛选器
      fetchData('', true);
    };

    initSelectedMonth();
  }, []);

  const handleDateFilterChange = (selectedMonthValue: moment.MomentInput) => {
    fetchData(selectedMonthValue);
  };

  // 表头点击排序：将 tea-component sortable addon 的排序值转换为接口 sort 参数并重新拉取数据
  const handleSortChange = (sortValue?: { by: string; order: string }[]) => {
    const newSortParams = !isEmpty(sortValue)
      ? (sortValue || []).map((item) => ({ key: item.by, value: item.order }))
      : [{ key: 'spend_to_date', value: 'desc' }];
    setSortParams(newSortParams);
    fetchData(selectedMonth, false, newSortParams);
  };

  const changeFilterProjects = (
    cascadeValueTemp = cascadeValue,
    statusValueTemp = statusValue,
    milestoneValueTemp = milestoneValue,
    projectListAllTemp = projectListAll,
    allProjectTemp = studioProjectList,
    _selectedMonthValueTemp = selectedMonth,
  ) => {
    let projectFilterList = [...projectListAllTemp];
    // 过滤statusValueTemp
    if (!(statusValueTemp?.includes('all') || statusValueTemp?.length === 0)) {
      const newProjectList = projectFilterList?.filter((project: any) => {
        return statusValueTemp?.includes(project?.status);
      });
      projectFilterList = [...(newProjectList || [])];
    }
    // 过滤milestoneValueTemp
    if (!(milestoneValueTemp?.includes('all') || milestoneValueTemp?.length === 0)) {
      const newProjectList = projectFilterList?.filter((project: any) => {
        return milestoneValueTemp?.includes(project?.milestone_category);
      });
      projectFilterList = [...(newProjectList || [])];
    }
    let projectsTemp = [...projectFilterList];

    // 过滤cascadeValueTemp
    if (!(cascadeValueTemp?.includes('all') || cascadeValueTemp?.length === 0)) {
      const idToTitle = new Map<string, string>();
      (allProjectTemp || []).forEach((studio: any) => {
        (studio?.projects || []).forEach((p: any) => {
          if (p?.project_id) {
            idToTitle.set(String(p.project_id), p?.game_title);
          }
        });
      });

      const gameTitles = cascadeValueTemp?.map((v) => idToTitle.get(v))?.filter(Boolean) as string[];
      const newProjectList = projectsTemp?.filter((project: any) => {
        return gameTitles?.includes(project?.game_title);
      });
      projectsTemp = [...(newProjectList || [])];
    }

    setProjects([...projectsTemp]);
  };

  return {
    projects,
    loading,
    handleDateFilterChange,
    handleSortChange,
    studioProjectList,
    totalPa,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    changeFilterProjects,
    cascadeInfoList,
    cascadeTreeOptions,
    isMoM,
    setIsMoM,
  };
};
